import { z } from "zod";
import { TemplateValueSchema } from "./appsTemplates";

/**
 * An App's behavior plan: named instances of registered definitions, view
 * action aliases and explicit outgoing-event policies.
 *
 * Two documents share this file. `BehaviorPlanSchema` is what an author writes
 * inside a v2 manifest. `CompiledBehaviorPlanSchema` is what the backend
 * validates and returns on open, with the origins, owned state and resolved
 * service bindings the browser needs.
 */

/** The authored plan and the catalog it compiles against. Versioned separately from the manifest. */
export const BEHAVIOR_PLAN_VERSION = 1;
export const BEHAVIOR_CATALOG_VERSION = 1;

/** Bounds keep a plan finite: no unbounded instance, edge or traversal counts. */
export const BEHAVIOR_LIMITS = {
  instances: 8,
  actions: 32,
  policies: 32,
  targetsPerPolicy: 8,
  dispatchDepth: 8,
} as const;

/** A behavior instance name, used in the owned state path and in policy keys. */
export const BehaviorInstanceNameSchema = z
  .string()
  .regex(
    /^[a-z][a-z0-9_]{0,31}$/,
    "Instance names start with a lowercase letter and use letters, digits or _.",
  );
export type BehaviorInstanceName = z.infer<typeof BehaviorInstanceNameSchema>;

/** A view action alias: the name a view's `on` binding uses for a behavior operation. */
export const BehaviorActionNameSchema = z
  .string()
  .regex(
    /^[a-z][a-zA-Z0-9_]{0,63}$/,
    "Action names start with a lowercase letter and use letters, digits or _.",
  );

/** State paths behaviors own. A view cannot write them directly. */
export const BEHAVIOR_STATE_ROOT = "/$behaviors";

/**
 * How a source payload becomes an operation's input. `identity` passes it
 * through, `discard` passes nothing, `adapter` names a registered adapter and
 * the template that feeds it.
 */
export const BehaviorPayloadMappingSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("identity") }),
  z.object({ kind: z.literal("discard") }),
  z.object({
    kind: z.literal("adapter"),
    adapter: z.string().min(1).max(64),
    /** Read from the source payload with the Apps `{ "$param": name }` template. */
    input: TemplateValueSchema,
  }),
]);
export type BehaviorPayloadMapping = z.infer<
  typeof BehaviorPayloadMappingSchema
>;

/** One operation a plan can target. */
export const BehaviorBindingSchema = z.object({
  instance: BehaviorInstanceNameSchema,
  operation: z.string().min(1).max(64),
  payload: BehaviorPayloadMappingSchema.default({ kind: "identity" }),
});
export type BehaviorBinding = z.infer<typeof BehaviorBindingSchema>;

export const AuthoredBehaviorInstanceSchema = z.object({
  definition: z.string().min(1).max(64),
  version: z.number().int().positive(),
  config: z.record(z.string(), z.unknown()).default({}),
  /** Service slot name to the manifest command that performs it. */
  services: z.record(z.string(), z.string().min(1).max(64)).default({}),
});
export type AuthoredBehaviorInstance = z.infer<
  typeof AuthoredBehaviorInstanceSchema
>;

/** The `behaviors` section of a v2 manifest. */
export const BehaviorPlanSchema = z.object({
  planVersion: z.literal(BEHAVIOR_PLAN_VERSION),
  instances: z.record(
    BehaviorInstanceNameSchema,
    AuthoredBehaviorInstanceSchema,
  ),
  /** View action name to the operation it means. */
  actions: z.record(BehaviorActionNameSchema, BehaviorBindingSchema),
  /** `"<instance>.<event>"` to its ordered targets. An empty array stops the fact. */
  policies: z.record(z.string().min(3).max(96), z.array(BehaviorBindingSchema)),
});
export type BehaviorPlan = z.infer<typeof BehaviorPlanSchema>;

// ── Compiled plan ───────────────────────────────────────────────────────────

export const CompiledBehaviorServiceSchema = z.object({
  command: z.string(),
  /** Params the operation supplies to the command's `{ "$param" }` templates. */
  params: z.array(z.string()).readonly(),
});

export const CompiledBehaviorInstanceSchema = z.object({
  name: BehaviorInstanceNameSchema,
  definition: z.string(),
  version: z.number().int().positive(),
  config: z.record(z.string(), z.unknown()),
  /** The instance's initial own state. */
  state: z.record(z.string(), z.unknown()),
  /** Where that state lives, `/$behaviors/<name>`. */
  statePath: z.string(),
  services: z.record(z.string(), CompiledBehaviorServiceSchema),
});
export type CompiledBehaviorInstance = z.infer<
  typeof CompiledBehaviorInstanceSchema
>;

export const BehaviorOriginSchema = z.object({
  element: z.string(),
  /** The element event, or `watch:<path>` for a state watcher. */
  event: z.string(),
  /** The binding's path inside the element, including nested continuations. */
  bindingPath: z.string(),
});
export type BehaviorOrigin = z.infer<typeof BehaviorOriginSchema>;

export const CompiledBehaviorActionSchema = BehaviorBindingSchema.extend({
  /** Every view binding that means this action. Empty when the alias is unused. */
  origins: z.array(BehaviorOriginSchema).readonly(),
});
export type CompiledBehaviorAction = z.infer<
  typeof CompiledBehaviorActionSchema
>;

export const CompiledBehaviorPolicySchema = z.object({
  instance: BehaviorInstanceNameSchema,
  event: z.string(),
  targets: z.array(BehaviorBindingSchema).readonly(),
  /** True when `targets` is empty: the fact stops here and nothing fails. */
  stop: z.boolean(),
});
export type CompiledBehaviorPolicy = z.infer<
  typeof CompiledBehaviorPolicySchema
>;

export const CompiledBehaviorPlanSchema = z.object({
  planVersion: z.literal(BEHAVIOR_PLAN_VERSION),
  catalogVersion: z.literal(BEHAVIOR_CATALOG_VERSION),
  instances: z.array(CompiledBehaviorInstanceSchema).readonly(),
  actions: z.record(z.string(), CompiledBehaviorActionSchema),
  policies: z.array(CompiledBehaviorPolicySchema).readonly(),
  /** State subtrees the host owns; a direct write is rejected. */
  ownedPaths: z.array(z.string()).readonly(),
});
export type CompiledBehaviorPlan = z.infer<typeof CompiledBehaviorPlanSchema>;

// ── Diagnostics ─────────────────────────────────────────────────────────────

export const BehaviorIssueCodeSchema = z.enum([
  "invalid-plan",
  "unknown-definition",
  "unsupported-definition-version",
  "unknown-operation",
  "invalid-config",
  "unknown-service-slot",
  "missing-service-slot",
  "unknown-command",
  "unsupported-command-param",
  "unknown-action",
  "action-collision",
  "unknown-event",
  "missing-policy",
  "unknown-instance",
  "unknown-adapter",
  "invalid-payload",
  "reserved-name",
  "unsupported-view-construct",
  "graph-cycle",
  "too-many-instances",
  "too-many-edges",
]);
export type BehaviorIssueCode = z.infer<typeof BehaviorIssueCodeSchema>;

/** A path-specific problem: where it is, what code it is, and what to do. */
export const BehaviorIssueSchema = z.object({
  code: BehaviorIssueCodeSchema,
  path: z.array(z.union([z.string(), z.number()])).readonly(),
  message: z.string(),
});
export type BehaviorIssue = z.infer<typeof BehaviorIssueSchema>;
